/**
 * Dónde va cada estado en el mosaico.
 *
 * No es un mapa geográfico: es una rejilla de casillas que **conserva la
 * posición relativa** de los estados —Baja California arriba a la izquierda,
 * Yucatán a la derecha— sin la silueta real. A esta escala es mejor: en un
 * mapa de verdad, Aguascalientes con un registro es un punto invisible y la
 * Ciudad de México con tres no se ve; en casillas iguales, cada estado pesa lo
 * que dice su número y no lo que mide su territorio.
 *
 * Las coordenadas están en el mismo `viewBox` de 710×360 del diseño original,
 * con casillas de 64 y paso de 70. Cambiar una clave mueve un estado de sitio,
 * así que son estables: las emite el servidor en `Cobertura.porEstado[].clave`.
 */

export interface CasillaDeEstado {
  clave: string;
  x: number;
  y: number;
}

export const LADO_DE_CASILLA = 64;
export const ANCHO_DEL_MOSAICO = 710;
export const ALTO_DEL_MOSAICO = 360;

export const CASILLAS: CasillaDeEstado[] = [
  { clave: 'BC', x: 8, y: 8 },
  { clave: 'SON', x: 78, y: 8 },
  { clave: 'CHH', x: 148, y: 8 },
  { clave: 'COA', x: 218, y: 8 },
  { clave: 'NL', x: 288, y: 8 },
  { clave: 'TAM', x: 358, y: 8 },

  { clave: 'BCS', x: 8, y: 78 },
  { clave: 'SIN', x: 78, y: 78 },
  { clave: 'DGO', x: 148, y: 78 },
  { clave: 'ZAC', x: 218, y: 78 },
  { clave: 'SLP', x: 288, y: 78 },

  { clave: 'NAY', x: 78, y: 148 },
  { clave: 'AGS', x: 148, y: 148 },
  { clave: 'GTO', x: 218, y: 148 },
  { clave: 'QRO', x: 288, y: 148 },
  { clave: 'HGO', x: 358, y: 148 },
  { clave: 'VER', x: 428, y: 148 },

  { clave: 'JAL', x: 78, y: 218 },
  { clave: 'MIC', x: 148, y: 218 },
  { clave: 'MEX', x: 218, y: 218 },
  { clave: 'CMX', x: 288, y: 218 },
  { clave: 'TLX', x: 358, y: 218 },
  { clave: 'PUE', x: 428, y: 218 },
  { clave: 'TAB', x: 498, y: 218 },
  { clave: 'YUC', x: 568, y: 218 },
  { clave: 'QROO', x: 638, y: 218 },

  { clave: 'COL', x: 78, y: 288 },
  { clave: 'GRO', x: 148, y: 288 },
  { clave: 'MOR', x: 218, y: 288 },
  { clave: 'OAX', x: 288, y: 288 },
  { clave: 'CHS', x: 358, y: 288 },
  { clave: 'CAM', x: 568, y: 288 },
];

/**
 * Intensidad de la casilla según cuántos registros tenga.
 *
 * Tres escalones y no un degradado continuo: con conteos de 1 a 3 un degradado
 * no se distingue, y lo que el mapa tiene que contestar de un vistazo es
 * "¿dónde hay algo y dónde no?", no "¿cuánto exactamente?" — para eso está el
 * número escrito dentro.
 */
export function intensidad(registros: number): 0 | 1 | 2 | 3 {
  if (registros <= 0) return 0;
  if (registros === 1) return 1;
  if (registros === 2) return 2;
  return 3;
}
